import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageSquare, X, Send, Loader2 } from "lucide-react";
import { API_BASE } from "@/lib/data";

const CHIPS = [
  "How much is a boiler service?",
  "My boiler has no pressure",
  "Book an emergency callout",
];

const getSessionId = () => {
  let id = localStorage.getItem("tg_chat_session");
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem("tg_chat_session", id);
  }
  return id;
};

export const ChatWidget = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: "assistant", content: "Hi, I'm Tigs — the Teign Gas virtual assistant. Ask me about prices, boiler problems, or booking an engineer." },
  ]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const bottomRef = useRef(null);
  const sessionId = useRef(getSessionId());

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, open]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || streaming) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: msg }, { role: "assistant", content: "" }]);
    setStreaming(true);
    try {
      const res = await fetch(`${API_BASE}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId.current, message: msg }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n\n");
        buffer = lines.pop();
        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          const payload = line.slice(6);
          if (payload === "[DONE]") continue;
          try {
            const { delta } = JSON.parse(payload);
            if (delta) {
              setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = { role: "assistant", content: copy[copy.length - 1].content + delta };
                return copy;
              });
            }
          } catch {}
        }
      }
    } catch {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: "Sorry, connection trouble. Call us on 07737 016265 and we'll help right away." };
        return copy;
      });
    } finally {
      setStreaming(false);
    }
  };

  return (
    <>
      <motion.button
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ delay: 1.6, type: "spring", stiffness: 200 }}
        onClick={() => setOpen(true)}
        data-testid="chat-open-btn"
        className={`fixed bottom-20 right-4 sm:bottom-6 sm:right-6 z-[60] bg-torch hover:bg-torch-hover text-white rounded-full p-4 shadow-[0_0_32px_rgba(255,85,0,0.5)] transition-all hover:scale-105 ${open ? "hidden" : ""}`}
        aria-label="Open chat"
      >
        <MessageSquare className="w-6 h-6" />
        <span className="absolute -top-1 -right-1 w-3.5 h-3.5 bg-emerald-400 rounded-full border-2 border-obsidian animate-pulse-dot" />
      </motion.button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.96 }}
            transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
            data-testid="chat-window"
            className="fixed bottom-20 right-4 sm:bottom-6 sm:right-6 z-[60] w-[calc(100vw-2rem)] sm:w-[380px] h-[540px] max-h-[70vh] bg-[#101010] border border-neutral-800 rounded-lg shadow-2xl flex flex-col overflow-hidden"
          >
            <div className="flex items-center justify-between px-5 py-4 border-b border-neutral-800 bg-[#0C0C0C]">
              <div className="flex items-center gap-3">
                <img src="/images/logo-flame.png" alt="Teign Gas" className="w-9 h-9 rounded-sm mix-blend-screen" />
                <div>
                  <p className="font-display font-bold text-white text-sm">Tigs — Teign Gas</p>
                  <p className="font-mono text-[10px] tracking-wider text-emerald-400 uppercase flex items-center gap-1.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" /> Online now
                  </p>
                </div>
              </div>
              <button onClick={() => setOpen(false)} data-testid="chat-close-btn" className="text-neutral-500 hover:text-white transition-colors" aria-label="Close chat">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div data-testid="chat-messages" className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div
                    className={`max-w-[85%] px-4 py-2.5 rounded-lg text-sm leading-relaxed ${
                      m.role === "user"
                        ? "bg-torch text-white rounded-br-sm"
                        : "bg-surface border border-neutral-800 text-neutral-200 rounded-bl-sm"
                    } ${streaming && i === messages.length - 1 && m.role === "assistant" ? "typing-cursor" : ""}`}
                  >
                    {m.content}
                  </div>
                </div>
              ))}
              <div ref={bottomRef} />
            </div>

            {messages.length <= 1 && (
              <div className="px-4 pb-2 flex flex-wrap gap-2">
                {CHIPS.map((c) => (
                  <button
                    key={c}
                    onClick={() => send(c)}
                    data-testid={`chat-chip-${c.slice(0, 12).toLowerCase().replace(/[^a-z0-9]/g, "-")}`}
                    className="font-mono text-[11px] text-torch border border-torch/40 hover:bg-torch/10 px-3 py-1.5 rounded-full transition-colors"
                  >
                    {c}
                  </button>
                ))}
              </div>
            )}

            <form
              onSubmit={(e) => { e.preventDefault(); send(); }}
              className="flex items-center gap-2 px-4 py-3 border-t border-neutral-800 bg-[#0C0C0C]"
            >
              <input
                data-testid="chat-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Ask about your boiler…"
                className="flex-1 bg-transparent outline-none text-sm text-white placeholder:text-neutral-600"
              />
              <button
                type="submit"
                disabled={streaming || !input.trim()}
                data-testid="chat-send-btn"
                className="bg-torch hover:bg-torch-hover disabled:opacity-50 text-white p-2.5 rounded-sm transition-colors"
                aria-label="Send message"
              >
                {streaming ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
